// Cœur de la traduction : fonctions PURES (aucun React). createT() fabrique la fonction t().
//
//   t('cart.title')                       → « Votre panier »
//   t('cart.total', { amount: 12.5 })     → « Total : {{amount, currency}} » → « Total : 12,50 € »
//   t('cart.items', { count: 3 })         → clés cart.items_one / items_other (/ _zero, _few, _many selon la langue)
//   t.rich('home.intro', {}, { lien: <Link to="/x" /> })   → phrase avec liens (voir rich.js)
//
// Une clé = « espace.chemin.dans.le.fichier » : l'espace est le nom du fichier (locales/<langue>/<espace>.json).
// Clé absente dans la langue → texte français ; absente en français aussi → chaîne vide (jamais
// de clé brute à l'écran) et un avertissement en développement.
import { DEFAULT_LANG } from './config';
import { makeFormat } from './format';
import { renderRich } from './rich';

const warned = new Set();
const warnOnce = (message) => {
  if (process.env.NODE_ENV === 'production' || warned.has(message)) return;
  warned.add(message);
  // eslint-disable-next-line no-console
  console.warn(`[i18n] ${message}`);
};

const walk = (node, parts) => {
  let cur = node;
  for (const p of parts) {
    if (cur && typeof cur === 'object' && Object.prototype.hasOwnProperty.call(cur, p)) cur = cur[p];
    else return undefined;
  }
  return cur;
};

// Cherche la chaîne d'un chemin ; avec `count`, cherche d'abord la forme de pluriel (« chemin_one »…).
const pick = (dict, parts, count, locale) => {
  if (!dict) return undefined;
  if (count !== undefined) {
    const parent = walk(dict, parts.slice(0, -1));
    const last = parts[parts.length - 1];
    if (parent && typeof parent === 'object') {
      const cats = [];
      if (count === 0) cats.push('zero');
      try { cats.push(new Intl.PluralRules(locale).select(count)); } catch (e) { /* locale inconnue */ }
      cats.push('other');
      for (const c of cats) {
        const v = parent[`${last}_${c}`];
        if (typeof v === 'string') return v;
      }
    }
  }
  const v = walk(dict, parts);
  return typeof v === 'string' ? v : undefined;
};

const VAR = /\{\{\s*([\w.]+)\s*(?:,\s*(\w+)(?::([^}]*?))?)?\s*\}\}/g;
export const interpolate = (text, vars, fmt) => text.replace(VAR, (match, name, format, arg) => {
  const value = vars ? vars[name] : undefined;
  if (value === undefined || value === null) { warnOnce(`variable {{${name}}} non fournie pour « ${text.slice(0, 50)} »`); return ''; }
  return format ? fmt.apply(format, value, arg) : String(value);
});

/**
 * @param lang      langue demandée ('en')
 * @param locale    locale Intl ('en-GB')
 * @param getDict   (langue, espace) → dictionnaire chargé ou undefined
 */
export const createT = ({ lang, locale, getDict }) => {
  const fmt = makeFormat(locale);
  const frLocale = 'fr-FR';

  const lookup = (key, vars) => {
    const parts = String(key).split('.');
    const ns = parts.shift();
    const count = vars && typeof vars.count === 'number' ? vars.count : undefined;
    let text;
    if (lang !== DEFAULT_LANG) text = pick(getDict(lang, ns), parts, count, locale);
    if (text === undefined) text = pick(getDict(DEFAULT_LANG, ns), parts, count, frLocale);
    if (text === undefined) warnOnce(`clé absente : ${key}`);
    return text === undefined ? '' : text;
  };

  const t = (key, vars) => interpolate(lookup(key, vars), vars, fmt);
  t.rich = (key, vars, components) => renderRich(lookup(key, vars), components, (s) => interpolate(s, vars, fmt));
  // « Texte de remplacement » : pour les contenus dont le FRANÇAIS vit ailleurs que dans les dictionnaires
  // (fichiers de données, base de données : questions de faq.json, fiches produit…). Renvoie la traduction
  // de la langue courante si elle existe, sinon `fallback` (le texte français d'origine). Ne consulte
  // jamais le dictionnaire français : t.or('faq.questions.offre.q', question.q)
  t.or = (key, fallback, vars) => {
    if (lang !== DEFAULT_LANG) {
      const parts = String(key).split('.');
      const ns = parts.shift();
      const text = pick(getDict(lang, ns), parts, vars && typeof vars.count === 'number' ? vars.count : undefined, locale);
      if (text !== undefined) return interpolate(text, vars, fmt);
    }
    return fallback;
  };
  t.has = (key) => {
    const parts = String(key).split('.');
    const ns = parts.shift();
    return pick(getDict(lang, ns), parts) !== undefined || pick(getDict(DEFAULT_LANG, ns), parts) !== undefined;
  };
  t.lang = lang;
  t.locale = locale;
  t.format = fmt;
  return t;
};
